import { handleActions } from 'redux-actions';
import { types } from './actions';
import { types as CreateWizardActionsTypes } from './createWizard/actions';
import { types as SessionActionsTypes } from '../session/actions';
import createWizardReducer, {
  initialState as createWizardInitialState
} from './createWizard/reducers';

const initialState = {
  queryUnitsIsInProgress: false,
  queryUnitsHasFailed: false,
  updateUnitByIdIsInProgress: false,
  updateUnitByIdHasFailed: false,
  updateUnitByIdSuccess: false,
  deleteUnitByIdIsInProgress: false,
  deleteUnitByIdHasFailed: false,
  deleteUnitByIdSuccess: false,
  findUnitByIdIsInProgress: false,
  findUnitByIdHasFailed: false,
  list: [],
  rentedUnits: 0,
  rentedUnitsTotalPages: -1,
  totalPages: -1,
  currentPage: 0,
  apiError: null,
  // currentUnit is the currently displayed unit
  // in the detail route
  currentUnit: null,
  updatedCurrentUnitCoordinates: {
    latitude: 0,
    longitude: 0
  },
  accessLevels: [],
  accessLevelsInProgress: false,
  accessLevelsHasFailed: false,
  zoneAccessAssignments: [],
  zoneAccessAssignmentsInProgress: false,
  zoneAccessAssignmentsHasFailed: false,
  zoneAccessAssignmentInProgress: false,
  zoneAccessAssignmentSuccess: false,
  zoneAccessAssignmentPending: false,
  zoneAccessAssignmentHasFailed: false,
  zoneAccessAssignmentApiError: null,
  zoneAccessAssignmentMessage: null,
  // Two-indicator sync feedback (Keenvil vs. Servidor de RecoFacial),
  // additive to the fields above: 'mode' tells the panel which alert style
  // to render. 'legacy' (DSS / community-ZK) keeps the single combined
  // alert above; 'twoStep' (ZK unit/individual) uses these instead.
  zoneAccessAssignmentMode: null,
  zoneAccessAssignmentKeenvilSaved: false,
  zoneAccessAssignmentRecofacialStatus: null,
  zoneAccessAssignmentRecofacialMessage: null,
  // Morosidad (access suspensions) — spec-front-morosidad-castleblack.md.
  unitAccessSuspensions: [],
  unitAccessSuspensionsInProgress: false,
  unitAccessSuspensionsHasFailed: false,
  unitAccessSuspensionsApiError: null,
  accessSuspensionInProgress: false,
  accessSuspensionSuccess: false,
  accessSuspensionHasFailed: false,
  accessSuspensionApiError: null,
  accessSuspensionMessage: null,
  accessSuspensionKeenvilSaved: false,
  accessSuspensionRecofacialStatus: null,
  accessSuspensionRecofacialMessage: null,
  createUnitWizard: createWizardInitialState,
  createUnitHasFailed: false,
  createUnitInProgress: false
};

const reducer = handleActions(
  {
    ...createWizardReducer,
    [CreateWizardActionsTypes.stopCreateUnitWizard]: state => {
      return {
        ...state,
        createUnitWizard: createWizardInitialState,
        apiError: null
      };
    },
    [types.queryRentedContractsSuccess]: (state, action) => {
      const { totalElements, totalPages } = action.payload;
      return {
        ...state,
        rentedUnits: totalElements,
        rentedUnitsTotalPages: totalPages
      };
    },
    [types.queryMultipleUnitsSuccess]: (state, action) => {
      const units = action.payload;
      return {
        ...state,
        queryUnitsIsInProgress: false,
        queryUnitsHasFailed: false,
        list: units,
        totalPages: units.totalPages,
        currentPage: units.number,
        apiError: null
      };
    },
    [types.queryUnitsSuccess]: (state, action) => {
      const units = action.payload;
      return {
        ...state,
        queryUnitsIsInProgress: false,
        queryUnitsHasFailed: false,
        list: units.content,
        totalPages: units.totalPages,
        totalElements: units.totalElements,
        currentPage: units.number,
        apiError: null
      };
    },
    [types.queryMultipleUnitsFailed]: (state, action) => {
      return {
        ...state,
        queryUnitsIsInProgress: false,
        queryUnitsHasFailed: true,
        list: [],
        totalPages: -1,
        currentPage: 0,
        apiError: action.payload.apiError
      };
    },
    [types.queryUnitsFailed]: (state, action) => {
      return {
        ...state,
        queryUnitsIsInProgress: false,
        queryUnitsHasFailed: true,
        list: [],
        totalPages: -1,
        currentPage: 0,
        apiError: action.payload.apiError
      };
    },
    [types.queryUnitsInProgress]: state => {
      return {
        ...state,
        queryUnitsIsInProgress: true,
        queryUnitsHasFailed: false,
        apiError: null
      };
    },
    [types.findUnitByIdSuccess]: (state, action) => {
      return {
        ...state,
        findUnitByIdIsInProgress: false,
        findUnitByIdHasFailed: false,
        currentUnit: action.payload,
        apiError: null,
        updatedCurrentUnitCoordinates: {
          latitude: action.payload.location
            ? action.payload.location.latitude
            : 0,
          longitude: action.payload.location
            ? action.payload.location.longitude
            : 0
        }
      };
    },
    [types.findUnitByIdInProgress]: state => {
      return {
        ...state,
        currentUnit: null,
        findUnitByIdIsInProgress: true,
        findUnitByIdHasFailed: false,
        apiError: null
      };
    },
    [types.findUnitByIdFailed]: (state, action) => {
      return {
        ...state,
        findUnitByIdIsInProgress: false,
        findUnitByIdHasFailed: true,
        currentUnit: null,
        apiError: action.payload.apiError
      };
    },
    [SessionActionsTypes.logOff]: () => {
      return initialState;
    },
    [types.deleteUnitByIdInProgress]: state => {
      return {
        ...state,
        deleteUnitByIdIsInProgress: true,
        deleteUnitByIdHasFailed: false,
        deleteUnitByIdSuccess: false,
        apiError: null
      };
    },
    [types.deleteUnitByIdSuccess]: state => {
      return {
        ...state,
        deleteUnitByIdIsInProgress: false,
        deleteUnitByIdHasFailed: false,
        deleteUnitByIdSuccess: true,
        apiError: null
      };
    },
    [types.deleteUnitByIdFailed]: (state, action) => {
      return {
        ...state,
        deleteUnitByIdIsInProgress: false,
        deleteUnitByIdHasFailed: true,
        deleteUnitByIdSuccess: false,
        apiError: action.payload.apiError
      };
    },
    [types.updateUnitByIdInProgress]: state => {
      return {
        ...state,
        updateUnitByIdIsInProgress: true,
        updateUnitByIdHasFailed: false,
        updateUnitByIdSuccess: false,
        apiError: null
      };
    },
    [types.updateUnitByIdSuccess]: state => {
      return {
        ...state,
        updateUnitByIdIsInProgress: false,
        updateUnitByIdHasFailed: false,
        updateUnitByIdSuccess: true,
        apiError: null
      };
    },
    [types.updateUnitByIdFailed]: (state, action) => {
      return {
        ...state,
        updateUnitByIdIsInProgress: false,
        updateUnitByIdHasFailed: true,
        updateUnitByIdSuccess: false,
        apiError: action.payload.apiError
      };
    },
    [types.createUnitSuccess]: state => {
      return {
        ...state,
        createUnitHasFailed: false,
        createUnitInProgress: false,
        apiError: null
      };
    },
    [types.createUnitInProgress]: state => {
      return {
        ...state,
        createUnitHasFailed: false,
        createUnitInProgress: true,
        apiError: null
      };
    },
    [types.createUnitFailed]: (state, action) => {
      return {
        ...state,
        createUnitHasFailed: true,
        createUnitInProgress: false,
        apiError: action.payload.apiError
      };
    },
    [types.setCurrentUnitCoordinates]: (state, action) => {
      return {
        ...state,
        updatedCurrentUnitCoordinates: action.payload
      };
    },
    [types.findAccessLevelsInProgress]: state => {
      return {
        ...state,
        accessLevelsInProgress: true,
        accessLevelsHasFailed: false
      };
    },
    [types.findAccessLevelsSuccess]: (state, action) => {
      return {
        ...state,
        accessLevels: action.payload,
        accessLevelsInProgress: false,
        accessLevelsHasFailed: false
      };
    },
    [types.findAccessLevelsFailed]: (state, action) => {
      return {
        ...state,
        accessLevels: [],
        accessLevelsInProgress: false,
        accessLevelsHasFailed: true,
        apiError: action.payload.apiError
      };
    },
    [types.findZoneAccessAssignmentsInProgress]: state => {
      return {
        ...state,
        zoneAccessAssignmentsInProgress: true,
        zoneAccessAssignmentsHasFailed: false
      };
    },
    [types.findZoneAccessAssignmentsSuccess]: (state, action) => {
      return {
        ...state,
        zoneAccessAssignments: action.payload,
        zoneAccessAssignmentsInProgress: false,
        zoneAccessAssignmentsHasFailed: false
      };
    },
    [types.findZoneAccessAssignmentsFailed]: (state, action) => {
      return {
        ...state,
        zoneAccessAssignments: [],
        zoneAccessAssignmentsInProgress: false,
        zoneAccessAssignmentsHasFailed: true,
        apiError: action.payload.apiError
      };
    },
    [types.updateZoneAccessAssignmentInProgress]: state => {
      return {
        ...state,
        zoneAccessAssignmentInProgress: true,
        zoneAccessAssignmentSuccess: false,
        zoneAccessAssignmentPending: false,
        zoneAccessAssignmentHasFailed: false,
        zoneAccessAssignmentApiError: null,
        zoneAccessAssignmentMessage: null,
        zoneAccessAssignmentMode: null,
        zoneAccessAssignmentKeenvilSaved: false,
        zoneAccessAssignmentRecofacialStatus: null,
        zoneAccessAssignmentRecofacialMessage: null
      };
    },
    [types.updateZoneAccessAssignmentSuccess]: (state, action) => {
      return {
        ...state,
        zoneAccessAssignmentInProgress: false,
        zoneAccessAssignmentSuccess: true,
        zoneAccessAssignmentPending: Boolean(action.payload.pending),
        zoneAccessAssignmentHasFailed: false,
        zoneAccessAssignmentApiError: null,
        zoneAccessAssignmentMessage: action.payload.message,
        zoneAccessAssignmentMode: 'legacy'
      };
    },
    [types.updateZoneAccessAssignmentFailed]: (state, action) => {
      return {
        ...state,
        zoneAccessAssignmentInProgress: false,
        zoneAccessAssignmentSuccess: false,
        zoneAccessAssignmentPending: false,
        zoneAccessAssignmentHasFailed: true,
        zoneAccessAssignmentApiError: action.payload.apiError,
        zoneAccessAssignmentMessage: null,
        zoneAccessAssignmentMode: 'legacy'
      };
    },
    // Two-indicator flow (ZK unit/individual) — see comment on initialState.
    [types.updateZoneAccessAssignmentKeenvilSaved]: (state, action) => {
      return {
        ...state,
        zoneAccessAssignmentInProgress: false,
        zoneAccessAssignmentMode: 'twoStep',
        zoneAccessAssignmentKeenvilSaved: true,
        zoneAccessAssignmentRecofacialStatus: 'loading',
        zoneAccessAssignmentRecofacialMessage: null,
        zoneAccessAssignmentMessage: action.payload.message,
        zoneAccessAssignmentApiError: null,
        zoneAccessAssignmentHasFailed: false
      };
    },
    [types.updateZoneAccessAssignmentRecofacialStatus]: (state, action) => {
      return {
        ...state,
        zoneAccessAssignmentRecofacialStatus: action.payload.status,
        zoneAccessAssignmentRecofacialMessage: action.payload.message
      };
    },
    [types.clearZoneAccessAssignmentStatus]: state => {
      return {
        ...state,
        zoneAccessAssignmentSuccess: false,
        zoneAccessAssignmentPending: false,
        zoneAccessAssignmentHasFailed: false,
        zoneAccessAssignmentApiError: null,
        zoneAccessAssignmentMessage: null,
        zoneAccessAssignmentMode: null,
        zoneAccessAssignmentKeenvilSaved: false,
        zoneAccessAssignmentRecofacialStatus: null,
        zoneAccessAssignmentRecofacialMessage: null
      };
    },
    [types.findUnitAccessSuspensionsInProgress]: state => {
      return {
        ...state,
        unitAccessSuspensionsInProgress: true,
        unitAccessSuspensionsHasFailed: false
      };
    },
    [types.findUnitAccessSuspensionsSuccess]: (state, action) => {
      return {
        ...state,
        unitAccessSuspensions: action.payload,
        unitAccessSuspensionsInProgress: false,
        unitAccessSuspensionsHasFailed: false
      };
    },
    [types.findUnitAccessSuspensionsFailed]: (state, action) => {
      return {
        ...state,
        unitAccessSuspensionsInProgress: false,
        unitAccessSuspensionsHasFailed: true,
        unitAccessSuspensionsApiError: action.payload.apiError
      };
    },
    [types.updateAccessSuspensionInProgress]: state => {
      return {
        ...state,
        accessSuspensionInProgress: true,
        accessSuspensionSuccess: false,
        accessSuspensionHasFailed: false,
        accessSuspensionApiError: null,
        accessSuspensionMessage: null,
        accessSuspensionKeenvilSaved: false,
        accessSuspensionRecofacialStatus: null,
        accessSuspensionRecofacialMessage: null
      };
    },
    [types.updateAccessSuspensionSuccess]: (state, action) => {
      return {
        ...state,
        accessSuspensionInProgress: false,
        accessSuspensionSuccess: true,
        accessSuspensionHasFailed: false,
        accessSuspensionApiError: null,
        accessSuspensionMessage: action.payload.message
      };
    },
    [types.updateAccessSuspensionFailed]: (state, action) => {
      return {
        ...state,
        accessSuspensionInProgress: false,
        accessSuspensionSuccess: false,
        accessSuspensionHasFailed: true,
        accessSuspensionApiError: action.payload.apiError,
        accessSuspensionMessage: null
      };
    },
    // Two-indicator flow — see comment on finishAccessSuspensionSync.
    [types.updateAccessSuspensionKeenvilSaved]: (state, action) => {
      return {
        ...state,
        accessSuspensionInProgress: false,
        accessSuspensionKeenvilSaved: true,
        accessSuspensionRecofacialStatus: 'loading',
        accessSuspensionRecofacialMessage: null,
        accessSuspensionMessage: action.payload.message,
        accessSuspensionApiError: null,
        accessSuspensionHasFailed: false
      };
    },
    [types.updateAccessSuspensionRecofacialStatus]: (state, action) => {
      return {
        ...state,
        accessSuspensionRecofacialStatus: action.payload.status,
        accessSuspensionRecofacialMessage: action.payload.message
      };
    },
    [types.clearAccessSuspensionStatus]: state => {
      return {
        ...state,
        accessSuspensionSuccess: false,
        accessSuspensionHasFailed: false,
        accessSuspensionApiError: null,
        accessSuspensionMessage: null,
        accessSuspensionKeenvilSaved: false,
        accessSuspensionRecofacialStatus: null,
        accessSuspensionRecofacialMessage: null
      };
    },
    [SessionActionsTypes.logOff]: () => {
      return initialState;
    }
  },
  initialState
);

export default reducer;
